import { type SVGProps } from 'react'

/**
 * Hand-drawn feel icons — 24×24, 1.6px stroke, rounded caps.
 * Match the style from design-system/index.html.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function I({ size = 24, ...rest }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      {...rest}
    />
  )
}

export const BottleIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M9 3h6M9 5h6M9 7l1 2h4l1-2M10 9v11a2 2 0 002 2h0a2 2 0 002-2V9M8 13h8" />
  </I>
)

export const DiaperIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M4 7h16l-2 8a4 4 0 01-4 3h-4a4 4 0 01-4-3L4 7zM7 12h10M9 15h6" />
  </I>
)

export const SleepIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    <path d="M15 5l1.5 1.5L15 8M18 8l1 1-1 1" />
  </I>
)

export const MedicationIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="4" y="8" width="16" height="10" rx="5" />
    <path d="M12 8v10M4 13h16" />
  </I>
)

export const NoteIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M4 4h12l4 4v12H4V4zM16 4v4h4M8 12h8M8 16h6" />
  </I>
)

export const HandoffIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="8" cy="8" r="3" />
    <circle cx="16" cy="8" r="3" />
    <path d="M5 20v-2a3 3 0 013-3h0M19 20v-2a3 3 0 00-3-3h0M10 12l4 0" />
  </I>
)

export const NeedIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </I>
)

export const HealthIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 21s-8-5-8-11a5 5 0 018-4 5 5 0 018 4c0 6-8 11-8 11z" />
  </I>
)

export const ShoppingIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M6 6h15l-1.5 9H8L6 6zM6 6L5 3H2M9 20a1 1 0 100-2 1 1 0 000 2zM18 20a1 1 0 100-2 1 1 0 000 2z" />
  </I>
)

export const SchoolIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 3l10 5-10 5-10-5 10-5zM6 10v4c0 2 3 3 6 3s6-1 6-3v-4" />
  </I>
)

export const SportsIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3l4 6-4 6-4-6 4-6zM3 12l6 4M21 12l-6 4" />
  </I>
)

export const ActivityIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 12h4l2-8 4 16 2-8h6" />
  </I>
)

export const PregnancyIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="8" r="3" />
    <path d="M9 12s-3 2-3 6a4 4 0 004 4h4a4 4 0 004-4c0-4-3-6-3-6" />
  </I>
)

export const SafetyIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6l8-3zM9 12l2 2 4-4" />
  </I>
)

export const CalendarIcon = (p: IconProps) => (
  <I {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </I>
)

export const ChevronRightIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M9 6l6 6-6 6" />
  </I>
)

export const ChevronLeftIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M15 6l-6 6 6 6" />
  </I>
)

export const PlusIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 5v14M5 12h14" />
  </I>
)

export const CloseIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M6 6l12 12M6 18L18 6" />
  </I>
)

export const CheckIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M5 12l5 5L20 6" />
  </I>
)

export const HomeIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M3 11l9-8 9 8v10a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1V11z" />
  </I>
)

export const TimelineIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M8 2v20M4 5l4-3 4 3M4 12l4-3 4 3M4 19l4-3 4 3" />
    <path d="M14 5h6M14 12h6M14 19h6" />
  </I>
)

export const AskIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M21 15a2 2 0 01-2 2H8l-5 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" />
    <path d="M9 10h.01M13 10h.01M17 10h.01" />
  </I>
)

export const SettingsIcon = (p: IconProps) => (
  <I {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z" />
  </I>
)

export const PeopleIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </I>
)

export const SparkleIcon = (p: IconProps) => (
  <I {...p}>
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zM19 15l.7 2.1L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.9L19 15z" />
  </I>
)

export const IconForPack: Record<string, (p: IconProps) => JSX.Element> = {
  early_care: BottleIcon,
  shopping:   ShoppingIcon,
  health:     HealthIcon,
  school:     SchoolIcon,
  sports:     SportsIcon,
  activity:   ActivityIcon,
  pregnancy:  PregnancyIcon,
  teen:       PeopleIcon,
  safety:     SafetyIcon,
  needs:      NeedIcon,
}
